import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { CheckoutProgressHeader } from "@shared/components/CheckoutProgressHeader/CheckoutProgressHeader";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import {
  buildSelfCheckoutSteps,
  getSelfCheckoutStepNumber,
} from "@shared/selfCheckoutFlow/buildSteps";
import { useSelfCheckoutFlow } from "@shared/selfCheckoutFlow/useSelfCheckoutFlow";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

interface Props {
  onBack?: () => void;
}

/**
 * Unauthenticated self-checkout entry header — thin wrapper over the
 * presentational CheckoutProgressHeader. The flow (Journey vs direct-lab) is
 * derived from the entry path, so the step count and labels match the
 * authenticated checkout header from the very first screen (no 3-to-4 step
 * jump after auth). Entry always highlights the first step.
 */
const JourneyCheckoutHeaderNav = ({ onBack }: Props) => {
  const { t } = useTranslation();
  const [isMobilePhlebotomyEnabled] = useFlippers("odyssey_journeys_mobile_phlebotomy");
  const flow = useSelfCheckoutFlow();

  const handleBackButtonPress = useCallback(() => onBack?.(), [onBack]);

  const steps = buildSelfCheckoutSteps(
    flow,
    { isMobilePhlebotomyEnabled },
    {
      patientInfo: t(l.journeyCheckout.header.stepLabels.patientInfo),
      address: t(l.journeyCheckout.header.stepLabels.address),
      sampleCollection: t(l.journeyCheckout.header.stepLabels.sampleCollection),
      payment: t(l.journeyCheckout.header.stepLabels.payment),
    }
  );

  return (
    <CheckoutProgressHeader>
      <CheckoutProgressHeader.Back
        onBack={handleBackButtonPress}
        label={t(l.journeyCheckout.header.backButton)}
      />
      <CheckoutProgressHeader.Steps
        steps={steps}
        currentStep={getSelfCheckoutStepNumber(steps, "patientInfo")}
      />
      <CheckoutProgressHeader.SecureBadge label={t(l.journeyCheckout.header.secureCheckout)} />
    </CheckoutProgressHeader>
  );
};

export { JourneyCheckoutHeaderNav };
